import React, { useState } from 'react';
import uploadToDropbox from '../../services/dropboxService';

function LifestyleForm({ onSubmit, onCancel, initialData }) {
  const [formData, setFormData] = useState(initialData || {
    name: '',
    description: '',
    photo: null,
    profileImageUrl: '',
    instagramHandle: '',
    twitterHandle: '',
    height: '',
    weight: '',
    age: ''
  });
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(initialData?.profileImageUrl || '');

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setError('Image must be less than 5MB');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      photo: file
    }));
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name.trim() || !formData.description.trim()) {
      setError('Please fill in all required fields');
      return;
    }
    
    if (!initialData && !formData.photo) {
      setError('Please upload a profile image');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      let profileImageUrl = formData.profileImageUrl;
      
      // Upload new photo if provided
      if (formData.photo) {
        profileImageUrl = await uploadToDropbox(formData.photo);
      }

      // Prepare final data with all fields
      const finalData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        profileImageUrl,
        instagramHandle: formData.instagramHandle?.trim() || null,
        twitterHandle: formData.twitterHandle?.trim() || null,
        height: formData.height ? parseFloat(formData.height) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        age: formData.age ? parseInt(formData.age) : null,
        ...(initialData?.id && { id: initialData.id })
      };

      await onSubmit(finalData);
      
      if (!initialData) {
        // Clear form if it's a new submission
        setFormData({
          name: '',
          description: '',
          photo: null,
          profileImageUrl: '',
          instagramHandle: '',
          twitterHandle: '',
          height: '',
          weight: '',
          age: ''
        });
        setPreviewUrl('');
      }
    } catch (err) {
      setError('Failed to process request: ' + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const removePhoto = () => {
    setFormData(prev => ({
      ...prev,
      photo: null,
      profileImageUrl: initialData?.profileImageUrl || ''
    }));
    setPreviewUrl(initialData?.profileImageUrl || '');
  };

  const handleInputChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  return (
    <div className="bg-gray-900/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {initialData ? 'Edit Model' : 'Add New Model'}
          </h2>
          <p className="text-gray-400">Fill in the details below</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-red-600/20 rounded-full">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-red-300">{initialData ? 'Editing' : 'Creating'}</span>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
          <div className="flex items-center gap-2 text-red-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-3">
              <span className="text-red-400">*</span> Model Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={handleInputChange('name')}
              className="w-full p-4 rounded-xl bg-gray-900/50 border border-gray-700 text-white placeholder-gray-500 focus:border-red-600 focus:ring-2 focus:ring-red-900/30 outline-none transition-all"
              placeholder="Enter model name"
              required
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm font-medium mb-3">
              Age
            </label>
            <input
              type="number"
              min="18"
              max="80"
              value={formData.age}
              onChange={handleInputChange('age')}
              className="w-full p-4 rounded-xl bg-gray-900/50 border border-gray-700 text-white placeholder-gray-500 focus:border-red-600 focus:ring-2 focus:ring-red-900/30 outline-none transition-all"
              placeholder="Enter age"
            />
          </div>
        </div>

        <div>
          <label className="block text-gray-300 text-sm font-medium mb-3">
            <span className="text-red-400">*</span> Description
          </label>
          <textarea
            value={formData.description}
            onChange={handleInputChange('description')}
            rows="4"
            className="w-full p-4 rounded-xl bg-gray-900/50 border border-gray-700 text-white placeholder-gray-500 focus:border-red-600 focus:ring-2 focus:ring-red-900/30 outline-none transition-all resize-none"
            placeholder="Describe the model..."
            required
          />
        </div>

        {/* Physical Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-3">
              Height (cm)
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={formData.height}
              onChange={handleInputChange('height')}
              className="w-full p-4 rounded-xl bg-gray-900/50 border border-gray-700 text-white placeholder-gray-500 focus:border-red-600 focus:ring-2 focus:ring-red-900/30 outline-none transition-all"
              placeholder="Enter height in cm"
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm font-medium mb-3">
              Weight (kg)
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={formData.weight}
              onChange={handleInputChange('weight')}
              className="w-full p-4 rounded-xl bg-gray-900/50 border border-gray-700 text-white placeholder-gray-500 focus:border-red-600 focus:ring-2 focus:ring-red-900/30 outline-none transition-all"
              placeholder="Enter weight in kg"
            />
          </div>
        </div>

        {/* Social Media Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-3">
              Instagram Handle
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <span className="text-gray-500">@</span>
              </div>
              <input
                type="text"
                value={formData.instagramHandle}
                onChange={handleInputChange('instagramHandle')}
                className="w-full p-4 pl-8 rounded-xl bg-gray-900/50 border border-gray-700 text-white placeholder-gray-500 focus:border-red-600 focus:ring-2 focus:ring-red-900/30 outline-none transition-all"
                placeholder="username"
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-300 text-sm font-medium mb-3">
              Twitter Handle
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <span className="text-gray-500">@</span>
              </div>
              <input
                type="text"
                value={formData.twitterHandle}
                onChange={handleInputChange('twitterHandle')}
                className="w-full p-4 pl-8 rounded-xl bg-gray-900/50 border border-gray-700 text-white placeholder-gray-500 focus:border-red-600 focus:ring-2 focus:ring-red-900/30 outline-none transition-all"
                placeholder="username"
              />
            </div>
          </div>
        </div>

        {/* Profile Image Upload Section */}
        <div>
          <label className="block text-gray-300 text-sm font-medium mb-3">
            {initialData ? 'Update Profile Image' : 'Profile Image'}
            <span className="text-red-400 ml-1">*</span>
          </label>
          
          <div className="space-y-4">
            {/* Current/Preview Image */}
            {(previewUrl || formData.profileImageUrl) && (
              <div className="relative w-32 h-32 rounded-xl overflow-hidden border-2 border-gray-700">
                <img 
                  src={previewUrl || formData.profileImageUrl} 
                  alt="Profile preview"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={removePhoto}
                  className="absolute top-2 right-2 w-6 h-6 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors"
                >
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {/* Upload Area */}
            <div className="border-2 border-dashed border-gray-700 rounded-2xl p-8 text-center hover:border-red-600/50 transition-colors">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                id="photo-upload"
                disabled={isUploading}
              />
              <label 
                htmlFor="photo-upload"
                className={`cursor-pointer flex flex-col items-center justify-center ${isUploading ? 'opacity-50' : ''}`}
              >
                <div className="w-16 h-16 bg-gray-800/50 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-white font-medium mb-2">
                  {previewUrl || formData.profileImageUrl ? 'Change Image' : 'Upload Profile Image'}
                </p>
                <p className="text-gray-400 text-sm">PNG, JPG, WEBP up to 5MB</p>
                <p className="text-gray-500 text-xs mt-2">Single image only</p>
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 pt-6 border-t border-gray-800">
          <button
            type="submit"
            disabled={isUploading}
            className="flex-1 px-6 py-4 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-300 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Processing...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {initialData ? 'Update Model' : 'Create Model'}
              </>
            )}
          </button>
          
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isUploading}
              className="px-6 py-4 bg-gray-800/50 hover:bg-gray-700/50 text-gray-300 hover:text-white rounded-xl transition-all duration-300 font-medium border border-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default LifestyleForm;